/**
 * PII scrubbing for Sentry events, breadcrumbs, spans and transactions.
 *
 * The staff app and the API handle guest passports / national IDs (the MRZ
 * scan in ./mrz), guest contact details, and message content. None of that may
 * reach Sentry: the only identities attached are opaque ids (a staff id from the
 * staff app and the MCP endpoint, the staff app's property id, and its native
 * SDKs' install id), there is no Session Replay on PII surfaces, and
 * `sendDefaultPii` is false. The MCP endpoint also records its tool inputs on
 * traces (docs/observability-privacy.md, "Identity"). These scrubbers are
 * the last line that strips anything sensitive which still made it into an
 * event, breadcrumb, span or transaction before it leaves the device / server.
 *
 * Every egress channel needs its OWN hook: the SDK dispatches to `beforeSend`
 * only for error events, to `beforeSendSpan` for the root span and each child
 * of a transaction (and for standalone span envelopes), and to
 * `beforeSendTransaction` for the transaction event itself — which is the only
 * one of the three that carries `request` (URL, query string, cookies,
 * headers). A hook that is not set is an unscrubbed channel. See
 * docs/observability-privacy.md for the coverage table.
 *
 * Pure functions with no `@sentry/*` dependency, so every SDK (capacitor, react,
 * nextjs, electron) wires them in the same way. Typed against a structural
 * subset of Sentry's event / span shape; the real SDK types satisfy it, so call
 * sites stay cast-free.
 */

export const REDACTED = "[redacted]";
const REDACTED_EMAIL = "[redacted-email]";
const REDACTED_ID = "[redacted-id]";

const MAX_DEPTH = 20;

// Keys redacted when they appear ANYWHERE in event/breadcrumb data. Matched
// case-insensitively as substrings, so `guestFirstName`, `firstName` and
// `first_name` all hit `firstname`. Chosen to catch guest PII + travel-document
// (MRZ) fields + secrets, while leaving useful debugging keys (ids, counts,
// status, component names) intact.
const SENSITIVE_KEY_SUBSTRINGS: readonly string[] = [
  // Travel document / MRZ — mirrors MRZFormData + mrzRawDataSchema in ./mrz
  "passport",
  "mrz",
  "documentnumber",
  "personalnumber",
  "nationalid",
  "idnumber",
  "dateofbirth",
  "birthdate",
  "dob",
  "dateofexpiry",
  "expirydate",
  "expiry",
  "issuingcountry",
  "issuingstate",
  "nationality",
  // Registration identity — place / country of birth, country + address
  // of residence, exemption reason, purpose of stay are guest PII a filing
  // carries. Bare `birth` / `residence` match both the camelCase app keys
  // (birthPlace) and the snake_case DB column names (birth_place); `exemption`
  // covers an exemption reason, which can name a GDPR special-category ground
  // (e.g. disability); `staypurpose` covers the medical / etc. stay purpose;
  // `taxnote` / `tax_note` cover staff's note on a stay's tax, which records
  // the same grounds ("checked her disability decision").
  "birth",
  "residence",
  "exemption",
  "staypurpose",
  "taxnote",
  "tax_note",
  // Names — first/last only; never bare "name" (would hit filename/displayName)
  "firstname",
  "lastname",
  "fullname",
  "guestname",
  "surname",
  "givenname",
  // Contact / location
  "email",
  "phone",
  "mobile",
  "telephone",
  "address",
  "postal",
  "postcode",
  "zipcode",
  // Network — forwarding headers carry the client IP (personal data under GDPR).
  // Both spellings: a header reaches app data hyphenated (`x-real-ip`) and a
  // span attribute underscored (`http.request.header.x_real_ip`), and the
  // underscore form also catches `cf_connecting_ip` / `true_client_ip`.
  "forwarded",
  "real-ip",
  "real_ip",
  "client-ip",
  "client_ip",
  "connecting-ip",
  "connecting_ip",
  "remote-addr",
  "remote_addr",
  // Secrets / auth — defense in depth; sendDefaultPii is already off
  "password",
  "secret",
  "apikey",
  "proxy-token",
  "proxy_token",
  "authorization",
  "cookie",
  "creditcard",
  "cardnumber",
  "ssn",
  "iban",
  "username",
];

// Short, ambiguous keys redacted only on an EXACT (lowercased) match, so we
// don't nuke substrings like "context" (contains "text") or "submission"
// (contains "sex"). `message`/`body`/`content`/`text` cover free-form guest
// message content carried inside nested data (never the top-level event title).
const SENSITIVE_KEY_EXACT: ReadonlySet<string> = new Set([
  "sex",
  "text",
  "body",
  "content",
  "message",
  "token",
]);

const EMAIL_RE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
// A run of MRZ alphabet (uppercase, digits, `<` filler) at least 20 chars long
// and containing a `<` is, in practice, a passport/ID MRZ line — `<` almost
// never occurs inside a 20-char alphanumeric run otherwise. Catches single TD1
// /TD2/TD3 lines and a full concatenated scan.
const MRZ_RE = /[A-Z0-9<]{20,}/g;

function isSensitiveKey(key: string): boolean {
  const k = key.toLowerCase();
  if (SENSITIVE_KEY_EXACT.has(k)) {
    return true;
  }
  return SENSITIVE_KEY_SUBSTRINGS.some((token) => k.includes(token));
}

function redactPii(value: string): string {
  return value
    .replace(EMAIL_RE, REDACTED_EMAIL)
    .replace(MRZ_RE, (match) => (match.includes("<") ? REDACTED_ID : match));
}

// Percent-encoding hides PII from both patterns: `%40` is the `@` EMAIL_RE
// needs, and `%3C` is the `<` that identifies an MRZ line. A URL-derived string
// (a span's `http.target`, a request URL, a breadcrumb) is where the encoded
// form actually turns up.
function percentDecode(value: string): string {
  if (!value.includes("%")) {
    return value;
  }
  try {
    return decodeURIComponent(value);
  } catch {
    // Malformed escapes (`%zz`, a lone `%`) throw; the raw form is all we have.
    return value;
  }
}

/**
 * Redact emails and MRZ strings embedded in free text, leaving the rest.
 *
 * Matched against the percent-decoded form as well as the raw one. When only
 * the decoded form is dirty the decoded, redacted string is returned: losing
 * the original encoding is the cost of not shipping the PII.
 */
function scrubString(value: string): string {
  const decoded = percentDecode(value);
  if (decoded === value) {
    return redactPii(value);
  }
  const redactedDecoded = redactPii(decoded);
  return redactedDecoded === decoded ? redactPii(value) : redactedDecoded;
}

const QUERY_FRAGMENT_RE = /[?#]/;
const URL_CREDENTIALS_RE = /^([a-z][a-z0-9+.-]*:\/\/)[^/@]*@/i;

/** Drop the query string (and fragment), which can carry tokens or PII. */
function stripQuery(url: string): string {
  const cut = url.search(QUERY_FRAGMENT_RE);
  return cut === -1 ? url : url.slice(0, cut);
}

/** Drop `user:password@` userinfo, which a DB connection string carries. */
function stripUrlCredentials(url: string): string {
  return url.replace(URL_CREDENTIALS_RE, "$1");
}

/** Keep the scheme, host and path of a URL; drop the query and any userinfo. */
function scrubUrlPath(url: string): string {
  return stripQuery(stripUrlCredentials(url));
}

/** Keep the scheme, host and path of a URL; drop everything that carries PII. */
function scrubUrl(url: string): string {
  return scrubString(scrubUrlPath(url));
}

// Headers whose value is a URL. `Referrer-Policy: strict-origin-when-cross-origin`
// sends the FULL url, query included, on a same-origin request — so the check-in
// page's own subrequests carry `?t=<jwt>` in `Referer` even though the scrubber
// cut it out of that page's own URL. `Location` carries the same on a redirect.
const URL_VALUED_HEADER_NAMES: ReadonlySet<string> = new Set([
  "location",
  "referer",
  "referrer",
]);

/**
 * Give URL-valued headers the treatment `request.url` gets.
 *
 * Runs before `scrubDeep`, which is key-based and would pass a referer through
 * as ordinary free text.
 */
function scrubRequestHeaders(headers: Record<string, unknown>): void {
  for (const key of Object.keys(headers)) {
    const value = headers[key];
    if (
      URL_VALUED_HEADER_NAMES.has(key.toLowerCase()) &&
      typeof value === "string"
    ) {
      headers[key] = scrubUrl(value);
    }
  }
}

// Recursively redact sensitive KEYS and scrub PII out of string VALUES. Mutates
// in place (Sentry hands us a throwaway event) and is guarded against cycles and
// runaway depth.
function scrubDeep(
  value: unknown,
  seen: WeakSet<object>,
  depth: number
): unknown {
  if (typeof value === "string") {
    return scrubString(value);
  }
  if (value === null || typeof value !== "object") {
    return value;
  }
  if (seen.has(value)) {
    return value;
  }
  // Past the depth cap, redact rather than pass the subtree through unscrubbed —
  // fail safe toward privacy on pathologically deep data.
  if (depth > MAX_DEPTH) {
    return REDACTED;
  }
  seen.add(value);

  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i += 1) {
      value[i] = scrubDeep(value[i], seen, depth + 1);
    }
    return value;
  }

  const record = value as Record<string, unknown>;
  for (const key of Object.keys(record)) {
    record[key] = isSensitiveKey(key)
      ? REDACTED
      : scrubDeep(record[key], seen, depth + 1);
  }
  return value;
}

/*
 * Attribute maps: a span's `data`, and an http breadcrumb's `data`.
 *
 * These are dotted OpenTelemetry keys, and the substring rules above were
 * written for camelCase / snake_case app data. Pointed at an attribute map they
 * get it wrong in both directions: they miss every URL-valued and IP-valued
 * key (nothing in the list matches `http.target` or `http.client_ip`), and they
 * destroy `server.address` because "address" is a substring. So attribute maps
 * get their own key policy, and reuse only the value-level scrubbing.
 *
 * The key lists are the SDK's own, read off the pinned build rather than
 * recalled: `getFetchSpanAttributes` (core `fetch.js`),
 * `getHttpSpanDetailsFromUrlObject` (core `utils/url.js`) and
 * `addOutgoingRequestBreadcrumb` are what decide what turns up here.
 */

// Attributes whose value IS a URL. The query string is what carries a guest
// check-in token (`?t=<jwt>`, a replayable credential for that guest's identity
// documents) and a registry search term (`?q=<guest name>`), so it is cut. The
// scheme, host and path stay: a trace whose URL is redacted to nothing cannot
// be debugged with, which is how a scrubber ends up switched off.
const URL_VALUED_ATTRIBUTE_KEYS: ReadonlySet<string> = new Set([
  "db.connection_string",
  "http.target",
  "http.url",
  "url",
  "url.full",
  "url.path",
  ...[...URL_VALUED_HEADER_NAMES].flatMap((header) => [
    `http.request.header.${header}`,
    `http.response.header.${header}`,
  ]),
]);

// Attributes whose value IS the query string or the fragment, with no path to
// keep. A client span and its breadcrumb carry `http.query`/`http.fragment`; a
// server span carries `url.query`/`url.fragment`.
const QUERY_VALUED_ATTRIBUTE_KEYS: ReadonlySet<string> = new Set([
  "http.fragment",
  "http.query",
  "http.query_string",
  "url.fragment",
  "url.query",
]);

// Attributes that are a guest's personal data whole: the MCP booking import's
// arguments (ADR-0145), one booking or a CSV of them, carry their name, contact
// details and notes, and the problems an agent reports at the end describe the
// bookings it could not send.
const PERSONAL_DATA_ATTRIBUTE_KEYS: ReadonlySet<string> = new Set([
  "mcp.request.argument.booking",
  "mcp.request.argument.csv",
  "mcp.request.argument.reportedproblems",
]);

// Attributes carrying the caller's IP address — personal data under GDPR, and
// `sendDefaultPii: false` does not suppress them: it governs what the SDK
// attaches to an EVENT, not what an instrumentation writes onto a span.
const IP_VALUED_ATTRIBUTE_KEYS: ReadonlySet<string> = new Set([
  "client.address",
  "http.client_ip",
  "net.host.ip",
  "net.peer.ip",
  "net.sock.host.addr",
  "net.sock.peer.addr",
  "network.local.address",
  "network.peer.address",
]);

// `server.address` is the DESTINATION host of an outgoing call ("which service
// did we call"), not a person — but "address" is in SENSITIVE_KEY_SUBSTRINGS,
// so without this exemption every client span would lose its target.
const KEYS_EXEMPT_FROM_SUBSTRINGS: ReadonlySet<string> = new Set([
  "server.address",
]);

// A span's name carries the URL in one of two shapes: `<METHOD> <target>` on an
// HTTP span, or the bare URL on a resource span (`resource.img`, `resource.css`
// and friends name the asset). Both anchored, so a db span's SQL — which uses
// `?` as a placeholder and is the whole diagnostic value of the span — is
// never cut: SQL neither starts with a method nor is a single URL token.
const HTTP_SPAN_DESCRIPTION_RE =
  /^(GET|POST|PUT|PATCH|DELETE|HEAD|OPTIONS|TRACE|CONNECT) (\S+)$/;
const BARE_URL_DESCRIPTION_RE = /^(https?:\/\/|\/)\S*$/i;

export interface ScrubbableSpan {
  data?: Record<string, unknown>;
  description?: string;
  op?: string;
}

function scrubAttributes(
  data: Record<string, unknown>,
  seen: WeakSet<object>
): void {
  for (const key of Object.keys(data)) {
    const value = data[key];
    if (
      IP_VALUED_ATTRIBUTE_KEYS.has(key) ||
      QUERY_VALUED_ATTRIBUTE_KEYS.has(key) ||
      PERSONAL_DATA_ATTRIBUTE_KEYS.has(key)
    ) {
      data[key] = REDACTED;
      continue;
    }
    if (URL_VALUED_ATTRIBUTE_KEYS.has(key)) {
      data[key] = typeof value === "string" ? scrubUrl(value) : REDACTED;
      continue;
    }
    const redactByKey =
      !KEYS_EXEMPT_FROM_SUBSTRINGS.has(key) && isSensitiveKey(key);
    data[key] = redactByKey ? REDACTED : scrubDeep(value, seen, 0);
  }
}

interface ScrubbableRequest {
  cookies?: unknown;
  data?: unknown;
  headers?: Record<string, unknown>;
  query_string?: unknown;
  url?: string;
}

export interface ScrubbableEvent {
  contexts?: Record<string, unknown>;
  exception?: { values?: Array<{ value?: string }> };
  extra?: Record<string, unknown>;
  message?: string;
  request?: ScrubbableRequest;
  tags?: Record<string, unknown>;
  user?: Record<string, unknown> | null;
}

export interface ScrubbableBreadcrumb {
  data?: Record<string, unknown>;
  message?: string;
}

// Keep the error title / exception messages (needed for grouping + debugging)
// but strip emails and MRZ strings interpolated into them.
function scrubEventMessage(event: ScrubbableEvent): void {
  if (typeof event.message === "string") {
    event.message = scrubString(event.message);
  }
  if (event.exception?.values) {
    for (const value of event.exception.values) {
      if (typeof value.value === "string") {
        value.value = scrubString(value.value);
      }
    }
  }
}

// Drop the highest-risk carriers outright. Bodies / cookies / query strings
// routinely contain guest PII, MRZ fields, message content or tokens.
function scrubEventRequest(
  event: ScrubbableEvent,
  seen: WeakSet<object>
): void {
  if (!(event.request && typeof event.request === "object")) {
    return;
  }
  delete event.request.cookies;
  delete event.request.data;
  delete event.request.query_string;
  if (event.request.headers) {
    scrubRequestHeaders(event.request.headers);
    scrubDeep(event.request.headers, seen, 0);
  }
  if (typeof event.request.url === "string") {
    // Drop the query string, then redact PII (e.g. an email) left in the path.
    event.request.url = scrubUrl(event.request.url);
  }
}

// Strip everything that could identify a person but an opaque `id`: the only
// identity Sentry.setUser may attach is the internal staff id, never email or
// name. See docs/observability-privacy.md, "Identity".
function scrubEventUser(event: ScrubbableEvent): void {
  if (!(event.user && typeof event.user === "object")) {
    return;
  }
  for (const key of Object.keys(event.user)) {
    if (key !== "id") {
      delete event.user[key];
    }
  }
}

/**
 * `beforeSend` hook: scrub an outgoing Sentry event in place and return it.
 * Never drops the event (we want the error, just sanitised).
 */
export function scrubEvent<T extends ScrubbableEvent>(event: T): T {
  if (!event || typeof event !== "object") {
    return event;
  }
  const seen = new WeakSet<object>();

  scrubEventMessage(event);
  scrubEventRequest(event, seen);
  scrubEventUser(event);

  if (event.extra) {
    scrubDeep(event.extra, seen, 0);
  }
  if (event.contexts) {
    scrubDeep(event.contexts, seen, 0);
  }
  if (event.tags) {
    scrubDeep(event.tags, seen, 0);
  }

  return event;
}

/**
 * `beforeBreadcrumb` hook: scrub a breadcrumb in place and return it. Console
 * args, fetch/xhr/navigation URLs and their data are the usual leak vectors —
 * and an http breadcrumb carries the query beside the sanitised URL rather than
 * inside it, so its data takes the same attribute policy a span's does.
 */
export function scrubBreadcrumb<T extends ScrubbableBreadcrumb>(
  breadcrumb: T
): T {
  if (!breadcrumb || typeof breadcrumb !== "object") {
    return breadcrumb;
  }
  const seen = new WeakSet<object>();

  if (typeof breadcrumb.message === "string") {
    breadcrumb.message = scrubString(breadcrumb.message);
  }

  if (breadcrumb.data && typeof breadcrumb.data === "object") {
    const data = breadcrumb.data as Record<string, unknown>;
    // `from` / `to` are the navigation breadcrumb's own URL keys, and belong to
    // no attribute vocabulary; everything else an http breadcrumb carries —
    // `url`, `http.query`, `http.fragment` — is in the attribute policy.
    for (const key of ["from", "to"]) {
      const value = data[key];
      if (typeof value === "string") {
        data[key] = scrubUrl(value);
      }
    }
    scrubAttributes(data, seen);
  }

  return breadcrumb;
}

function scrubSpanDescription(span: ScrubbableSpan): void {
  const { description } = span;
  if (typeof description !== "string") {
    return;
  }
  let withoutQuery = description;
  if (HTTP_SPAN_DESCRIPTION_RE.test(description)) {
    withoutQuery = description.replace(
      HTTP_SPAN_DESCRIPTION_RE,
      (_match, method: string, target: string) =>
        `${method} ${scrubUrlPath(target)}`
    );
  } else if (BARE_URL_DESCRIPTION_RE.test(description)) {
    withoutQuery = scrubUrlPath(description);
  }
  span.description = scrubString(withoutQuery);
}

/**
 * `beforeSendSpan` hook: scrub a span in place and return it.
 *
 * ALWAYS returns the span. Returning null/undefined does NOT drop it — the SDK
 * logs a warning and ships the ORIGINAL, unscrubbed span
 * (`@sentry/core` client.js `processedSpans.push(span)`, envelope.js
 * `createSpanEnvelope`, captureSpan.js `applyBeforeSendSpanCallback`), so a
 * defensive `return null` here would leak the raw payload rather than suppress
 * it. Drop spans with `ignoreSpans` instead.
 */
export function scrubSpan<T extends ScrubbableSpan>(span: T): T {
  if (!span || typeof span !== "object") {
    return span;
  }
  const seen = new WeakSet<object>();

  scrubSpanDescription(span);
  if (span.data && typeof span.data === "object") {
    scrubAttributes(span.data, seen);
  }

  return span;
}

// `contexts.trace` is span attributes, so it takes the span key policy — the
// event rules would destroy `server.address` and miss `http.target`. Every
// other context is arbitrary `Sentry.setContext` data and takes the event
// rules, which is what `scrubEvent` gives an error carrying the same payload.
function scrubTransactionContexts(
  event: ScrubbableEvent,
  seen: WeakSet<object>
): void {
  const { contexts } = event;
  if (!(contexts && typeof contexts === "object")) {
    return;
  }
  for (const key of Object.keys(contexts)) {
    if (key !== "trace") {
      contexts[key] = scrubDeep(contexts[key], seen, 0);
      continue;
    }
    const trace = contexts.trace as { data?: unknown } | null | undefined;
    if (trace?.data && typeof trace.data === "object") {
      scrubAttributes(trace.data as Record<string, unknown>, seen);
    }
  }
}

/**
 * `beforeSendTransaction` hook: scrub a transaction event in place and return
 * it.
 *
 * The child spans of a transaction are NOT this function's job —
 * `beforeSendSpan` runs first and owns them, `contexts.trace` included. What
 * only this hook can reach is `request`, which a captured envelope shows
 * carrying the full URL, the raw `query_string`, the cookie jar and the
 * Authorization header on every server transaction, and the contexts a
 * `Sentry.setContext` call attached.
 *
 * ALWAYS returns the event: unlike `beforeSendSpan`, returning null here really
 * does drop the transaction, which would silently disable tracing.
 */
export function scrubTransaction<T extends ScrubbableEvent>(event: T): T {
  if (!event || typeof event !== "object") {
    return event;
  }
  const seen = new WeakSet<object>();

  scrubEventRequest(event, seen);
  scrubEventUser(event);
  scrubTransactionContexts(event, seen);

  if (event.extra) {
    scrubDeep(event.extra, seen, 0);
  }
  if (event.tags) {
    scrubDeep(event.tags, seen, 0);
  }

  return event;
}

/**
 * The privacy options every Sentry init spreads: a scrubber on each egress
 * hook, structured logs off (their `beforeSendLog` has no scrubber, and the
 * SDK's default for them varies by version), and no IP, cookies or headers
 * auto-attached. Metrics stay at each init: which key turns them off depends
 * on the SDK (docs/observability-privacy.md).
 */
export const sentryPrivacyOptions = {
  beforeBreadcrumb: scrubBreadcrumb,
  beforeSend: scrubEvent,
  beforeSendSpan: scrubSpan,
  beforeSendTransaction: scrubTransaction,
  enableLogs: false,
  sendDefaultPii: false,
} as const;
